import type { UseMutationReturnType } from "@tanstack/vue-query";
import axios, { type AxiosError } from "axios";
import { computed, type Ref, ref, toValue } from "vue";

interface ConsolidatedAsyncStateProps {
  queryLoading: Record<string, Ref<boolean>>;
  queryErrors: Record<string, Ref<Error | Error[] | undefined>>;
  mutations: Record<string, UseMutationReturnType<unknown, Error, unknown, unknown>>;
}

export const useConsolidatedAsyncState = (
  props: ConsolidatedAsyncStateProps = {
    queryLoading: {},
    queryErrors: {},
    mutations: {},
  },
) => {
  const isLoadingRegister = ref<Record<string, Ref<boolean>>>(props.queryLoading);
  const loadingErrorsRegister = ref<Record<string, Ref<Error | Error[] | undefined>>>(props.queryErrors);
  const mutationRegister = ref<Record<string, UseMutationReturnType<unknown, Error, unknown, unknown>>>(
    props.mutations,
  );

  const registerLoadingState = (
    key: string = "default",
    isLoading: Ref<boolean>,
    loadingErrors: Ref<Error | Error[] | undefined>,
  ) => {
    isLoadingRegister.value[key] = isLoading;
    if (loadingErrors) {
      loadingErrorsRegister.value[key] = loadingErrors;
    }
  };

  const registeredLoadingErrors = computed<Error[]>(() => {
    let errors: Error[] = [];
    const refsWithErrors = Object.values(loadingErrorsRegister.value)
      .map(toValue)
      .filter((error): error is Error | Error[] => Boolean(error));
    refsWithErrors.forEach((error) => {
      if (Array.isArray(error)) {
        errors = [...errors, ...error];
      } else {
        errors.push(error);
      }
    });
    return errors;
  });

  const removeLoadingState = (key: string = "default"): void => {
    delete isLoadingRegister.value[key];
    delete loadingErrorsRegister.value[key];
  };

  const registerMutationState = (
    key: string = "default",
    mutation?: UseMutationReturnType<unknown, Error, unknown, unknown>,
  ) => {
    if (mutation) {
      mutationRegister.value[key] = mutation;
    }
  };

  const removeMutationState = (key: string = "default"): void => {
    delete mutationRegister.value[key];
  };

  const isLoading = computed<boolean>(() => {
    return Object.values(isLoadingRegister.value).some(toValue);
  });

  const isMutating = computed<boolean>(() => {
    return (
      Object.values(mutationRegister.value)
        .map((mutation) => mutation.isPending)
        .filter(Boolean).length > 0
    );
  });

  const loadingErrors = computed<Error[]>(() => {
    return Object.values(registeredLoadingErrors.value).map(toValue).filter(Boolean);
  });

  const hasLoadingErrors = computed<boolean>(() => {
    return loadingErrors.value.length > 0;
  });

  const mutationErrors = computed<Error[]>(() => {
    return Object.values(mutationRegister.value)
      .map((mutation) => mutation.error)
      .map(toValue)
      .filter((error): error is Error => Boolean(error));
  });

  const hasMutationErrors = computed<boolean>(() => {
    return mutationErrors.value.length > 0;
  });

  const allApplicationErrors = computed<AxiosError[]>(() =>
    [...loadingErrors.value, ...mutationErrors.value].filter((error) => axios.isAxiosError(error)),
  );

  const hasPermissionErrors = computed<boolean>(() =>
    allApplicationErrors.value.some((error) => error.response?.status === 403 || error.response?.status === 401),
  );

  const hasApplicationErrors = computed<boolean>(() =>
    allApplicationErrors.value.some((error) => error.response?.status !== 403 && error.response?.status !== 401),
  );

  return {
    registerLoadingState,
    registeredLoadingErrors,
    removeLoadingState,
    registerMutationState,
    removeMutationState,
    isLoading,
    loadingErrors,
    hasLoadingErrors,
    isMutating,
    mutationErrors,
    hasMutationErrors,
    hasPermissionErrors,
    hasApplicationErrors,
  };
};
